package com.fireavert.yardi.common_data.data_models.responses

import kotlinx.serialization.Serializable

/**
 * Response for the Yardi unit data import operation
 * Provides detailed information about the success and failure of unit imports
 */
@Serializable
data class YardiImportUnitDataResponse(
    /**
     * Overall success status of the import operation
     */
    val success: Boolean,
    
    /**
     * Total number of units attempted to import
     */
    val totalUnitsAttempted: Int,
    
    /**
     * Number of units successfully imported
     */
    val unitsSuccessfullyImported: Int,
    
    /**
     * Number of units that failed to import
     */
    val unitsFailedToImport: Int,
    
    /**
     * Number of units skipped (not attempted)
     */
    val unitsSkipped: Int,
    
    /**
     * Summary of the import operation
     */
    val summary: ImportSummary,
    
    /**
     * Detailed results for each unit
     */
    val unitResults: List<UnitImportResult>,
    
    /**
     * Any general errors that occurred during the import process
     */
    val generalErrors: List<String> = emptyList()
)

/**
 * Summary of the import operation with categorized counts
 */
@Serializable
data class ImportSummary(
    /**
     * Number of new tenants created
     */
    val newTenantsCreated: Int,
    
    /**
     * Number of units that had no mapping in the provided map
     */
    val unitsWithNoMapping: Int,
    
    /**
     * Number of units that had no current resident
     */
    val unitsWithNoCurrentResident: Int,
    
    /**
     * Number of units where the Connect unit was not found
     */
    val unitsWithMissingConnectUnit: Int,
    
    /**
     * Number of units that failed due to database errors
     */
    val unitsWithDatabaseErrors: Int,
    
    /**
     * Number of units that failed during tenant creation
     */
    val unitsWithTenantCreationErrors: Int
)

/**
 * Detailed result for a single unit import attempt
 */
@Serializable
data class UnitImportResult(
    /**
     * Yardi unit ID
     */
    val yardiUnitId: String,
    
    /**
     * Yardi unit number
     */
    val yardiUnitNumber: String? = null,
    
    /**
     * Connect unit ID if mapped
     */
    val connectUnitId: Int? = null,
    
    /**
     * Connect unit number if found
     */
    val connectUnitNumber: String? = null,
    
    /**
     * Status of the import for this unit
     */
    val status: UnitImportStatus,
    
    /**
     * Reason for failure or skip
     */
    val reason: String? = null,
    
    /**
     * Tenant information if successfully created
     */
    val tenantInfo: ImportedTenantInfo? = null,
    
    /**
     * Any error message associated with this unit
     */
    val errorMessage: String? = null
)

/**
 * Status of a unit import
 */
@Serializable
enum class UnitImportStatus {
    /**
     * Unit was successfully imported with tenant created
     */
    SUCCESS,
    
    /**
     * Unit was skipped due to missing mapping
     */
    SKIPPED_NO_MAPPING,
    
    /**
     * Unit was skipped due to missing Connect unit
     */
    SKIPPED_NO_CONNECT_UNIT,
    
    /**
     * Unit was skipped due to no current resident
     */
    SKIPPED_NO_CURRENT_RESIDENT,
    
    /**
     * Unit import failed due to database error
     */
    FAILED_DATABASE_ERROR,
    
    /**
     * Unit import failed during tenant creation
     */
    FAILED_TENANT_CREATION,
    
    /**
     * Unit import failed for other reasons
     */
    FAILED_OTHER
}

/**
 * Information about an imported tenant
 */
@Serializable
data class ImportedTenantInfo(
    /**
     * Created tenant ID in Connect
     */
    val tenantId: Int,
    
    /**
     * Tenant's first name
     */
    val firstName: String,
    
    /**
     * Tenant's last name
     */
    val lastName: String,
    
    /**
     * Tenant's email if available
     */
    val email: String? = null,
    
    /**
     * Tenant's phone number if available
     */
    val phoneNumber: String? = null,
    
    /**
     * Yardi resident ID
     */
    val yardiResidentId: String
)
